/**
 * 通用 React ErrorBoundary。
 *
 * 为什么需要它：
 * React 18 中，渲染期抛出的错误（包括 R3F <Canvas> 内 three.js 同步错误、
 * Suspense 里的 throw、子树任意 render 错误）若无人接住，会卸载整棵组件树，
 * 表现为整页白屏。本组件把这些错误拦在边界内，渲染降级 UI 而不是让用户看着空白。
 *
 * 设计：
 * - fallback 可为 ReactNode，或 (error, reset) => ReactNode 的渲染函数，
 *   便于上层自定义"重试"按钮调用 reset() 重新挂载子树。
 * - onReset：用户点重试时的回调（如重置外部 state、重新触发请求）。
 * - onError：错误埋点钩子（上报 Sentry / GA4 等），不影响渲染。
 * - reset 通过把内部 hasError 置回 false 实现；若子树本身未变，再次渲染可能再次抛错，
 *   调用方通常配合改变 key（强制重挂）或先修复触发条件再 reset。
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import i18n from "../i18n";
import { AlertTriangle } from "./icons";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** 出错时的降级 UI；可为渲染函数，拿到 error 与 reset。 */
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  /** 用户触发重试时的额外回调。 */
  onReset?: () => void;
  /** 错误埋点钩子（不抛、不影响渲染）。 */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // 本地留痕，便于排查；上报交给 onError 钩子（如有）。
    console.error("[ErrorBoundary] caught render error:", error, info);
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    const { fallback } = this.props;
    if (typeof fallback === "function") return fallback(error, this.reset);
    if (fallback !== undefined) return fallback;
    // 默认降级：避免调用方忘传 fallback 时仍白屏。用 i18n.t（类组件无法用 hook）。
    return (
      <div className="error-boundary-fallback" role="alert">
        <p>
          <AlertTriangle size={16} /> {i18n.t("errors.boundaryDefault")}
        </p>
        <button type="button" onClick={this.reset}>
          {i18n.t("common.retry")}
        </button>
      </div>
    );
  }
}
